import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import { Search, X, ArrowRight, TrendingUp, ShieldCheck, Calculator, ScrollText, GitCompareArrows, MapPin, Car, ChevronRight } from "lucide-react";
import { getSearchIndex, TYPE_LABELS, TYPE_ORDER } from "../lib/searchIndex";

// Iter-172 — Most Popular destinations. Mirrors the mobile drawer's
// quick-jump strip into the desktop command palette so Cmd/Ctrl+K
// power-users get the same 3-route speed boost as mobile users.
const MOST_POPULAR_DESTINATIONS = [
  {
    label: "Compare Insurance",
    description: "Compare rates from trusted providers.",
    path: "/auto-insurance",
    Icon: ShieldCheck,
  },
  {
    label: "Compare Auto Loans",
    description: "Find financing with competitive APRs.",
    path: "/auto-loans",
    Icon: Car,
  },
  {
    label: "Use Calculators",
    description: "Estimate savings in seconds.",
    path: "/calculators",
    Icon: Calculator,
  },
];

// Iter-68: suggested-search chips for the empty-state. Each chip either
// jumps to a known route (`path`) OR drops a query string into the search
// box (`query`) so the user can refine. Mirrors The Zebra / NerdWallet /
// Bankrate's "Popular searches" empty-state pattern that materially lifts
// search-feature engagement.
const SUGGESTED_SEARCHES = [
  { label: "Auto insurance", Icon: ShieldCheck, path: "/auto-insurance" },
  { label: "Refinance my car loan", Icon: TrendingUp, path: "/refinance" },
  { label: "Auto loan calculator", Icon: Calculator, path: "/auto-loan-calculator" },
  { label: "Best auto loans", Icon: ScrollText, path: "/best-auto-loan-lenders" },
  { label: "Lease vs. finance", Icon: GitCompareArrows, path: "/lease-vs-buy" },
  { label: "Cheap insurance by state", Icon: MapPin, query: "cheapest insurance" },
];

const FUSE_OPTIONS = {
  keys: [
    { name: "title", weight: 0.55 },
    { name: "subtitle", weight: 0.2 },
    { name: "keywords", weight: 0.25 },
  ],
  threshold: 0.34,
  ignoreLocation: true,
  minMatchCharLength: 2,
  includeScore: true,
};

const TYPE_PILL_STYLES = {
  article: "bg-[#0A2540] text-white",
  roundup: "bg-[#10B981] text-[#0A2540]",
  tool: "bg-amber-100 text-amber-900",
  compare: "bg-violet-100 text-violet-900",
  glossary: "bg-slate-100 text-slate-700",
  state: "bg-sky-100 text-sky-900",
  city: "bg-emerald-50 text-emerald-900",
};

// Up to 4 results per group, max 16 total — keeps the panel scan-able.
const PER_GROUP_LIMIT = 4;
const TOTAL_LIMIT = 16;

export default function SearchModal({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  // Build index on first open. Static slice is available instantly; articles
  // hydrate async via the same hook.
  useEffect(() => {
    if (!open) return;
    const { immediate, promise } = getSearchIndex();
    setIndex(immediate);
    setHydrated(false);
    promise.then((full) => {
      setIndex(full);
      setHydrated(true);
    });
  }, [open]);

  // Focus the input and lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset query on close
  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIdx(0);
    }
  }, [open]);

  const fuse = useMemo(
    () => (index.length > 0 ? new Fuse(index, FUSE_OPTIONS) : null),
    [index]
  );

  // Compute grouped + flat results.
  const { grouped, flat } = useMemo(() => {
    if (!fuse || query.trim().length < 2) {
      return { grouped: {}, flat: [] };
    }
    const hits = fuse.search(query.trim()).slice(0, 60);
    const byType = {};
    for (const { item } of hits) {
      if (!byType[item.type]) byType[item.type] = [];
      if (byType[item.type].length < PER_GROUP_LIMIT) byType[item.type].push(item);
    }
    const flatList = [];
    for (const type of TYPE_ORDER) {
      for (const item of byType[type] || []) {
        if (flatList.length >= TOTAL_LIMIT) break;
        flatList.push(item);
      }
    }
    return { grouped: byType, flat: flatList };
  }, [fuse, query]);

  // Reset active row on every query change
  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const handleNavigate = (item) => {
    onClose();
    setTimeout(() => navigate(item.path), 50);
  };

  // Keyboard navigation: Esc closes, ↑/↓ move, Enter opens.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (flat.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(flat.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flat[activeIdx];
        if (item) handleNavigate(item);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flat, activeIdx]);

  if (!open) return null;

  const empty = query.trim().length >= 2 && flat.length === 0;
  const tooShort = query.trim().length < 2;

  let flatPosition = -1;

  return (
    <div
      data-testid="search-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Site search"
      className="fixed inset-0 z-[100]"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        data-testid="search-modal-backdrop"
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
      />

      {/* Panel */}
      <div className="relative mx-auto max-w-2xl mt-[8vh] px-4">
        <div className="bg-white shadow-2xl border border-slate-200 overflow-hidden">
          {/* Iter-82: leading search icon for visual grounding. Replaces
              the bare input row — premium fintech command palettes (Stripe,
              Linear) anchor the field with a left-side icon. */}
          <div className="flex items-center gap-3 px-5 lg:px-6 h-16 border-b border-slate-200">
            <Search className="w-5 h-5 text-slate-500 shrink-0" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides, calculators, comparisons, states…"
              data-testid="search-modal-input"
              className="flex-1 bg-transparent outline-none border-none text-base font-display font-semibold text-slate-950 placeholder:text-slate-500"
            />
            <button
              type="button"
              onClick={onClose}
              data-testid="search-modal-close"
              aria-label="Close search"
              className="w-11 h-11 grid place-items-center text-slate-500 hover:text-slate-950 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto" data-testid="search-modal-results">
            {tooShort && (
              <div
                data-testid="search-modal-suggestions"
                className="px-5 pt-5 pb-6"
              >
                {/* Iter-172 — Most Popular destinations row. Mirrors the
                    mobile drawer's quick-jump strip into the desktop
                    command palette. Three high-converting routes for
                    Cmd/Ctrl+K power users in a hurry. */}
                <p
                  data-testid="search-modal-most-popular-label"
                  className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-3"
                >
                  Most Popular
                </p>
                <ul data-testid="search-modal-most-popular" className="space-y-1 mb-6">
                  {MOST_POPULAR_DESTINATIONS.map((d) => {
                    const Icon = d.Icon;
                    return (
                      <li key={d.path}>
                        <button
                          type="button"
                          data-testid={`search-most-popular-${d.path.replace(/\//g, "")}`}
                          onClick={() => {
                            onClose();
                            setTimeout(() => navigate(d.path), 50);
                          }}
                          className="group w-full flex items-center gap-3 px-3 py-2.5 -mx-3 text-left transition-colors duration-200 hover:bg-[#FAFAFA] active:bg-[#10B981]/8"
                        >
                          <Icon
                            className="w-6 h-6 text-[#10B981] shrink-0 opacity-90 group-hover:opacity-100 transition-opacity duration-200"
                            strokeWidth={2}
                            aria-hidden
                          />
                          <span className="flex-1 min-w-0">
                            <span className="block font-display font-bold text-[14px] leading-tight text-slate-950">
                              {d.label}
                            </span>
                            <span className="block text-[12px] text-slate-500 leading-snug mt-0.5">
                              {d.description}
                            </span>
                          </span>
                          <ChevronRight
                            className="w-4 h-4 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-1 transition-all duration-200 shrink-0"
                            aria-hidden
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-3">
                  Popular searches
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED_SEARCHES.map((s) => {
                    const Icon = s.Icon;
                    return (
                      <button
                        key={s.label}
                        type="button"
                        data-testid={`search-suggestion-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                        onClick={() => {
                          if (s.path) {
                            onClose();
                            setTimeout(() => navigate(s.path), 50);
                          } else if (s.query) {
                            setQuery(s.query);
                            inputRef.current?.focus();
                          }
                        }}
                        className="group inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-950 bg-slate-50 hover:bg-[#10B981]/10 border border-slate-200 hover:border-[#10B981]/40 px-3 py-2 transition-colors"
                      >
                        <Icon className="w-3.5 h-3.5 text-[#10B981]" strokeWidth={2.5} />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
                  Or type to search 100+ guides, 15 calculators, comparisons, and insurance + loan pages across 50 states + D.C.
                </p>
              </div>
            )}

            {empty && (
              <div
                data-testid="search-modal-empty"
                className="px-5 py-10 text-center text-sm text-slate-500"
              >
                No matches for <span className="font-bold text-slate-950">&quot;{query}&quot;</span>.
              </div>
            )}

            {!tooShort && !empty &&
              TYPE_ORDER.map((type) => {
                const items = grouped[type];
                if (!items || items.length === 0) return null;
                return (
                  <section
                    key={type}
                    data-testid={`search-group-${type}`}
                    className="border-b border-slate-100 last:border-b-0"
                  >
                    <div className="px-5 pt-4 pb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                      {TYPE_LABELS[type]}
                    </div>
                    <ul>
                      {items.map((item) => {
                        flatPosition += 1;
                        const isActive = flatPosition === activeIdx;
                        return (
                          <li key={`${item.type}-${item.path}`}>
                            <button
                              type="button"
                              onMouseEnter={() => setActiveIdx(flatPosition)}
                              onClick={() => handleNavigate(item)}
                              data-testid={`search-result-${item.type}-${item.path.replace(/\//g, "_")}`}
                              data-active={isActive ? "true" : "false"}
                              className={`group w-full text-left flex items-start gap-3 px-5 py-3 transition-colors ${
                                isActive ? "bg-[#10B981]/8" : "hover:bg-slate-50"
                              }`}
                            >
                              <span
                                className={`mt-0.5 text-[9px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 ${
                                  TYPE_PILL_STYLES[item.type] || "bg-slate-100 text-slate-700"
                                }`}
                              >
                                {TYPE_LABELS[item.type]}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block font-display font-bold text-[15px] text-slate-950 leading-snug">
                                  {item.title}
                                </span>
                                {item.subtitle && (
                                  <span className="block text-xs text-slate-500 mt-0.5 line-clamp-2 leading-snug">
                                    {item.subtitle}
                                  </span>
                                )}
                              </span>
                              <ArrowRight
                                className={`w-4 h-4 shrink-0 mt-1.5 transition-colors ${
                                  isActive ? "text-[#10B981]" : "text-slate-300"
                                }`}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
          </div>

          {/* Footer with kbd hints */}
          <div className="hidden sm:flex items-center justify-between gap-4 px-5 py-2.5 bg-slate-50/60 border-t border-slate-200 text-[11px] text-slate-500">
            <div className="flex items-center gap-3">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              <span>to navigate</span>
              <Kbd>↵</Kbd>
              <span>to open</span>
              <Kbd>esc</Kbd>
              <span>to close</span>
            </div>
            <div className="text-slate-500">
              {flat.length > 0
                ? `${flat.length} result${flat.length === 1 ? "" : "s"}`
                : ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 bg-white border border-slate-200 text-slate-700 font-bold rounded-sm">
      {children}
    </kbd>
  );
}
